import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import { RESERVATION_TYPE } from 'src/features/reservations/types/reservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { badgeReservationStatusOptions, badgeReservationTypeOptions } from 'src/features/reservations/utils/utils';
import {
    CellBadge,
    CellDatetime,
    CellIndex,
    DataTableColumnGenericHeaderDefinition,
    DataTableColumnHeader,
} from 'src/registry/components/table';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';

import type { ReservationsVehicleStayList } from 'src/features/reservations/types/reservationType';

const columnHelper = createColumnHelper<ReservationsVehicleStayList>();

export const VehiclesReservationsTableColumns = () => {
    const { formatMessage } = useIntl();
    const [searchParams] = useSearchParams();

    return [
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'id',
            cell: CellIndex,
            maxSize: 45,
            meta: { title: '#' },
        }),
        columnHelper.accessor('license_plate', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return (
                    <div>
                        <div className="font-medium">{row.original.license_plate}</div>
                        <div className="text-gray-600">{row.original.number ? `#${row.original.number}` : ''}</div>
                    </div>
                );
            },
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(reservationMessages.reservation),
            },
        }),
        columnHelper.accessor('client_name', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return (
                    <div>
                        <div className="font-medium">{row.original.client_name}</div>
                        <div className="text-gray-600">{row.original.client_email}</div>
                    </div>
                );
            },
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(reservationMessages.customer),
            },
        }),
        columnHelper.accessor((row) => row.vehicle_stay_reservation_type.reservation_type, {
            header: DataTableColumnHeader,
            cell: (props) => {
                const rowReservationType = props.row.original.vehicle_stay_reservation_type;
                const version =
                    rowReservationType.reservation_type !== RESERVATION_TYPE.FIXED_DURATION
                        ? `v${rowReservationType.version_number} - `
                        : '';
                return (
                    <div>
                        <CellBadge badgeOptions={badgeReservationTypeOptions} {...props} />
                        <div className="w-full whitespace-nowrap text-gray-600">{`${version} ${rowReservationType.name}`}</div>
                    </div>
                );
            },
            enableHiding: true,
            enableColumnFilter: !['hourly', 'daily'].includes(searchParams.get('reservation_type') || ''),
            enableSorting: !['hourly', 'daily'].includes(searchParams.get('reservation_type') || ''),
            meta: {
                title: formatMessage(reservationMessages.type),
                badgeOptions: badgeReservationTypeOptions,
            },
            id: 'reservation_type',
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            cell: CellBadge,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(reservationMessages.status),
                badgeOptions: badgeReservationStatusOptions,
            },
        }),
        columnHelper.accessor('from_time', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(reservationMessages.reservationValidFrom),
                inputType: INPUT_TYPE.DATE,
                inlineDateFilter: true,
                dateFilter: {
                    afterDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
                    beforeDate: new Date(),
                },
                filterLabel: formatMessage(reservationMessages.reservationValidFrom),
            },
        }),
        columnHelper.accessor('to_time', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(reservationMessages.reservationValidTo),
                inputType: INPUT_TYPE.DATE,
                inlineDateFilter: true,
                dateFilter: {
                    afterDate: new Date(),
                    beforeDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                },
                filterLabel: formatMessage(reservationMessages.reservationValidTo),
            },
        }),
        columnHelper.accessor('created_at', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(reservationMessages.createdAt),
            },
        }),
    ];
};
